import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, CreditCard, Image, FolderPlus, Share2, 
  User, Instagram, DollarSign, ArrowRight, Loader2
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const NUDGE_ICONS = {
  stripe_connect: CreditCard,
  add_portfolio: Image,
  create_gallery: FolderPlus,
  share_profile: Share2,
  complete_profile: User,
  add_socials: Instagram,
  enable_sales: DollarSign
};

const DashboardNudges = ({ photographer }) => {
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);

  useEffect(() => {
    fetchNudges();
    
    // Refetch nudges when the page becomes visible again (user returns to dashboard)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNudges();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Also refetch when photographer data changes
  useEffect(() => {
    if (photographer) {
      fetchNudges();
    }
  }, [photographer?.stripe_connect_status, photographer?.tagline, photographer?.bio, photographer?.social_links]);

  const fetchNudges = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/nudges`, { withCredentials: true });
      setNudges(response.data.nudges || []);
    } catch (error) {
      console.error("Failed to fetch nudges:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (nudge) => {
    const action = nudge.cta_action;

    if (action === "stripe_connect") {
      try {
        const currentUrl = window.location.origin;
        const response = await axios.post(`${API}/stripe/connect/onboard`, {
          return_url: `${currentUrl}/dashboard?stripe=complete`,
          refresh_url: `${currentUrl}/dashboard?stripe=refresh`
        }, { 
          withCredentials: true 
        });
        if (response.data.onboarding_url) {
          window.location.href = response.data.onboarding_url;
        } else if (response.data.status === "complete") {
          toast.success("Stripe account already connected!");
          fetchNudges();
        }
      } catch (error) {
        toast.error("Failed to start Stripe setup");
      }
    } else if (action === "copy_profile_link") {
      const slug = photographer?.slug;
      if (slug) {
        const url = `${window.location.origin}/photographer/${slug}`;
        navigator.clipboard.writeText(url);
        toast.success("Profile link copied!");
        
        // Record share click
        axios.post(`${API}/onboarding/share-clicked`, {}, { withCredentials: true });
        
        // Remove this nudge from local state
        setNudges(prev => prev.filter(n => n.id !== nudge.id));
      }
    } else if (action.startsWith("navigate:")) {
      const path = action.replace("navigate:", "");
      navigate(path);
    }
  };

  const handleDismiss = async (nudge, snooze = false) => {
    setDismissing(nudge.id);
    try {
      await axios.post(`${API}/onboarding/nudges/${nudge.id}/dismiss`, {
        nudge_id: nudge.id,
        snooze_days: snooze ? (nudge.snooze_days || 7) : null
      }, { withCredentials: true });
      
      setNudges(prev => prev.filter(n => n.id !== nudge.id));
      toast.success(snooze ? `Reminded in ${nudge.snooze_days || 7} days` : "Got it!");
    } catch (error) {
      toast.error("Failed to dismiss");
    } finally {
      setDismissing(null);
    }
  };

  if (loading || nudges.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6" data-testid="dashboard-nudges">
      <AnimatePresence mode="popLayout">
        {nudges.map((nudge, index) => {
          const Icon = NUDGE_ICONS[nudge.id] || User;
          
          return (
            <motion.div
              key={nudge.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm mb-1">{nudge.title}</h4>
                      <p className="text-sm text-muted-foreground">{nudge.message}</p>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button 
                        size="sm" 
                        onClick={() => handleAction(nudge)}
                        data-testid={`nudge-cta-${nudge.id}`}
                      >
                        {nudge.cta_text}
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                      
                      {nudge.dismissable && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDismiss(nudge, !!nudge.snooze_days)}
                          disabled={dismissing === nudge.id}
                        >
                          {dismissing === nudge.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default DashboardNudges;
